/**
 * SegmentGroup — el conmutador de dos o tres opciones excluyentes.
 *
 * ⚠️ El estado activo es **`aria-selected`, no una clase**. Es la decisión más
 * útil del sistema: `.cl-seg__item[aria-selected=true]` es lo único que lo
 * pinta, así que **no hay forma de que se vea elegido y el lector de pantalla
 * no lo sepa**. Con una clase `--activo` las dos cosas se pueden desincronizar
 * y nadie lo nota.
 *
 * El repo ya tiene un `SegmentGroup` propio en `modules/payroll`; este es el
 * del sistema, con `role="tablist"` y la altura por token.
 */
import type { HTMLAttributes, ReactNode } from 'react'

export interface SegmentOption<T extends string = string> {
  value: T
  label: ReactNode
  icon?: ReactNode
  /** Sale como `title`: explica la opción sin alargar el rótulo. */
  hint?: string
}

export interface SegmentGroupProps<T extends string = string>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'onChange'> {
  options?: SegmentOption<T>[]
  value?: T
  onChange?: (value: T) => void
  /** Obligatorio de hecho: sin él el grupo es una lista de botones sin nombre. */
  label?: string
  size?: 'sm' | 'md'
  className?: string
}

export function SegmentGroup<T extends string = string>({
  options = [],
  value,
  onChange,
  label,
  size = 'md',
  className = '',
  ...rest
}: SegmentGroupProps<T>) {
  return (
    <div className={`cl-seg ${className}`.trim()} role="tablist" aria-label={label} {...rest}>
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          role="tab"
          className="cl-seg__item"
          aria-selected={value === o.value}
          title={o.hint}
          style={size === 'sm' ? { padding: '4px 9px', fontSize: 10.5 } : undefined}
          onClick={() => onChange?.(o.value)}
        >
          {o.icon && (
            <span aria-hidden="true" style={{ display: 'flex' }}>
              {o.icon}
            </span>
          )}
          {o.label}
        </button>
      ))}
    </div>
  )
}
